export type ChatLinkTarget =
  | { kind: 'brand' | 'product' | 'competitor'; href: string; brandId: number; productId?: number; competitorId?: number; createProduct?: boolean; competitorTab?: 'fbads'; brandSection?: 'overview' | 'identity' }
  | { kind: 'image' | 'video' | 'source'; href: string };

export function chatLinkTarget(value?: string): ChatLinkTarget | null {
  if (!value || /[\u0000-\u0020\u007f]/.test(value)) return null;
  const branding = /^\/brands\/([1-9]\d*)\/branding\/?$/.exec(value);
  if (branding && Number.isSafeInteger(Number(branding[1]))) return { kind: 'brand', href: value, brandId: Number(branding[1]), brandSection: 'identity' };
  const createProduct = /^\/brands\/([1-9]\d*)\/products\/new\/?$/.exec(value);
  if (createProduct && Number.isSafeInteger(Number(createProduct[1]))) return { kind: 'product', href: value, brandId: Number(createProduct[1]), createProduct: true };
  const internal = /^\/brands\/(\d+)(?:\/(products|competitors)\/(\d+)(\/ads)?)?\/?$/.exec(value);
  if (internal && (!internal[4] || internal[2] === 'competitors')) return { kind: internal[2] === 'competitors' ? 'competitor' : internal[2] ? 'product' : 'brand', href: value, brandId: Number(internal[1]), ...(internal[3] ? internal[2] === 'competitors' ? { competitorId: Number(internal[3]), ...(internal[4]?{competitorTab:'fbads' as const}:{}) } : { productId: Number(internal[3]) } : {}) };
  try {
    const url = new URL(value);
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return null;
    const kind = /\.(?:png|jpe?g|gif|webp|avif|svg)$/i.test(url.pathname) ? 'image' : /\.(?:mp4|webm|mov)$/i.test(url.pathname) ? 'video' : 'source';
    return { kind, href: url.href };
  } catch { return null; }
}

export function chatLinkLabel(href: string, label = ''): string {
  const clean = label.replace(/<[^>]*>/g, '').trim();
  if (clean && !/(?:https?:\/\/|www\.)/i.test(clean) && clean !== href) return clean;
  const target = chatLinkTarget(href);
  if (target?.kind === 'image') return /\/products\//i.test(href) ? 'View product photo' : 'View image';
  if (target?.kind === 'video') return 'View video';
  if (target?.kind === 'product') return target.createProduct ? 'Add a product' : 'View product';
  if (target?.kind === 'competitor') return 'View competitor';
  if (target?.kind === 'brand') return target.brandSection === 'identity' ? 'View Branding' : 'View brand';
  return 'Open source';
}

// Convert legacy/model-produced anchors to Markdown; never enable raw HTML rendering.
export function normalizeChatLinks(content: string): string {
  const prose = content.replace(/```(?:html|text|markdown)?\s*\n([\s\S]*?)\n```/gi, (block, body: string) => /^(?:https?:\/\/[^\s]+|<a\b[\s\S]*<\/a\s*>)$/i.test(body.trim()) ? body.trim() : block).replace(/`(<a\b[^`]*<\/a\s*>)`/gi, '$1');
  return prose.replace(/<a\b([^>]*)>([\s\S]*?)<\/a\s*>/gi, (_all, attributes: string, body: string) => {
    const attr = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attributes);
    const href = (attr?.[1] || attr?.[2] || attr?.[3] || '').replace(/&amp;/g, '&');
    const label = chatLinkLabel(href, body).replace(/[\[\]\\]/g, '\\$&');
    return chatLinkTarget(href) ? `[${label}](<${href.replace(/>/g, '%3E')}>)` : label;
  }).replace(/`(https?:\/\/[^`\s]+)`/g, '$1');
}
